import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { EvidenceFocus } from "../../../annotation";
import { classesAbove, isEvidenceIndividual, type AboxViewProfile, type Individual, type ShownStatement, type StatementReferences, type ValueReadings } from "../../../knowledgeData";
import { termName, type TermCards } from "../../../ontology";
import ConclusionsSection from "./conclusions/ConclusionsSection";
import type { Conclusion } from "./conclusions/regestConclusions";
import EntityCards from "./entities/EntityCards";
import { entityGroups } from "./entities/regestEntities";
import type { PlacedStatement, RegestPlacement } from "./regestParts";

export interface RegestSectionsProps {
  /** `<document>@<version>`, whose spans count as words in this text. */
  readonly document: string;
  /** The lines beside the parts and in the unplaced list, conclusions taken out. */
  readonly lines: readonly ShownStatement<PlacedStatement>[];
  /** `conclusionsOf(all lines, profile.conclusions)`, numbered after `lines`. */
  readonly conclusions: readonly Conclusion[];
  /** "#n kinds" by statement key, as the bars carry it. */
  readonly labels: ReadonlyMap<string, string>;
  readonly numbers: ReadonlyMap<string, number>;
  readonly focus: EvidenceFocus;
  readonly profile: AboxViewProfile;
  readonly cards: ReadonlyMap<string, TermCards | null>;
  readonly individuals: readonly Individual[];
  readonly placement: RegestPlacement;
  readonly askInProject: string | undefined;
  readonly extra: ((statement: PlacedStatement) => ReactNode) | undefined;
  /** How dates and amounts read, the same as beside the text (`TextWithStatements`' memo). */
  readonly readings: ValueReadings | undefined;
  /** The regest's statements by what each is about, the same as beside the text, so a conclusion names them too. */
  readonly references?: StatementReferences;
}

/**
 * What Daniel's regest viewer shows after a vita's texts, in his order: the conclusions, then the
 * persons, places and benefices (`render_vita`). Both follow the project's view profile (C-B7 AB-5)
 * and the regest's focus, so a card or a premise leads to the words and line it stands for.
 */
export default function RegestSections({ document, lines, conclusions, labels, numbers, focus, profile, cards, individuals, placement, askInProject, extra, readings, references }: RegestSectionsProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  const shown = useMemo(() => [...lines, ...conclusions.map((conclusion) => conclusion.line)], [lines, conclusions]);
  const groups = useMemo(() => {
    // A class's parents from the first model version shown that knows it.
    const parentsOf = (iri: string) => [...cards.values()].find((terms) => terms?.has(iri) === true)?.get(iri)?.parents ?? [];
    return entityGroups({
      document,
      lines: shown,
      individuals,
      isEvidence: (individual) => isEvidenceIndividual(individual, profile, parentsOf),
      classesAbove: (classes) => classesAbove(classes, parentsOf),
      links: profile.entityLinks,
      partOf: new Map([...placement.parts].flatMap(([segmentPath, placed]) => placed.map((statement) => [statement.key, segmentPath] as const))),
    }, profile.entities);
  }, [document, shown, individuals, profile, cards, placement]);
  const lineOf = new Map(shown.map((line) => [line.shown.key, line]));
  // A line without a number, such as a relation, by what it says: the label of its first property.
  const labelOf = (key: string) => {
    const label = labels.get(key);
    if (label !== undefined) return label;
    const line = lineOf.get(key);
    const property = line?.layout.said.flatMap((description) => description.rows)[0]?.property;
    return line === undefined || property === undefined ? t("sources.document.regest.unnamed") : termName(property, cards.get(line.shown.modelCommit) ?? null, language);
  };
  const commit = shown[0]?.shown.modelCommit ?? null;
  return (
    <>
      {profile.conclusions === null ? null : (
        <ConclusionsSection
          conclusions={conclusions}
          shape={profile.conclusions}
          labelOf={labelOf}
          numbers={numbers}
          cards={cards}
          focus={focus}
          askInProject={askInProject}
          extra={extra}
          statementShape={profile.statements}
          readings={readings}
          references={references}
        />
      )}
      <EntityCards groups={groups} labelOf={labelOf} focus={focus} terms={{ commit, cards: commit === null ? null : cards.get(commit) ?? null }} />
    </>
  );
}
